<template>
    <div class="relative md:px-10 product-gallery mx-auto max-w-xl lg:max-w-[90%]" ref="galleryRef">
        <ClientOnly fallback-tag="span" fallback="Loading images...">
            <template #fallback>
                <p class="text-center text-gray-500">Loading product images...</p>
            </template>

            <!-- Main product image swiper -->
            <div v-if="isVisible" class="relative w-full mb-4 overflow-hidden main-slider-container">
                <Swiper ref="swiper2" class="relative overflow-hidden main-slider swiper-top border border-[#D9D9D9]" :slidesPerView="1" :spaceBetween="10"
                    :loop="true" @init="onSwiper2">
                    <SwiperSlide v-for="(image, index) in productImages" :key="`main-${index}`">
                        <img :src="image" class="object-contain w-full h-auto main-image" width="554" height="554" sizes="xs:360px sm:554px md:554px lg:484px xl:525px" format="webp" loading="lazy" :alt="productImageAlt" />
                    </SwiperSlide>
                </Swiper>


                <div class="absolute z-10 cursor-pointer swiper-button-prev-product -translate-y-2/4 top-2/4 left-4"
                    @click="prevSlide">

                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="19.2587" cy="19.2587" r="19.2587" transform="matrix(-1 0 0 1 39.3184 0.496094)"
                            fill="#D9D9D9" />
                        <path
                            d="M23.4102 12.4469L22.3269 11.3809L13.3621 20.1729L22.3269 28.9649L23.4102 27.9043L15.534 20.1729L23.4102 12.4469Z"
                            fill="black" />
                    </svg>

                </div>
                <div class="absolute z-10 cursor-pointer right-4 swiper-button-next-product -translate-y-2/4 top-2/4"
                    @click="nextSlide">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none"
                        xmlns="http://www.w3.org/2000/svg">
                        <circle cx="19.2587" cy="19.2587" r="19.2587" fill="#D9D9D9" />
                        <path
                            d="M16.5898 27.5531L17.6731 28.6191L26.6379 19.8271L17.6731 11.0351L16.5898 12.0957L24.466 19.8271L16.5898 27.5531Z"
                            fill="black" />
                    </svg>

                </div>
            </div>

            <!-- Thumbnails swiper -->
            <div v-if="isVisible" class="mt-4 thumbs-container sliper-bottom">
                <Swiper ref="swiper1" class="thumbs-swiper" :slidesPerView="4" 
                    :breakpoints="{
                        0: {
                            spaceBetween: 10
                        },
                        768: {
                            spaceBetween: 20
                        }
                    }"
                    :watchSlidesProgress="true" @init="onSwiper1">
                    <SwiperSlide v-for="(image, index) in productImages" :key="`thumb-${index}`"
                        class="cursor-pointer thumbnail-slide" :class="[swiperIndex2 === index ? 'active-thumbnail' : '']" @click="changeSlide(index)">
                        <div class="flex border border-transparent rounded thumbnail-wrapper">
                            <img sizes="xs:71px sm:127px md:127px lg:102px xl:111px" width="127" height="127" format="webp" loading="lazy" :src="image" class="object-contain w-full h-auto m-auto rounded thumbnail-image" :alt="productImageAlt" />
                        </div>
                    </SwiperSlide>
                </Swiper>
            </div>

            <!-- Placeholder when section is not visible -->
            <div v-else class="flex flex-col w-full gap-4 product-gallery-placeholder">
                <div class="placeholder-main w-full h-[544px] bg-gray-100 border border-[#D9D9D9] rounded"></div>
                <div class="placeholder-thumbs flex w-full md:h-[121px] gap-[10px]"></div>
            </div>
        </ClientOnly>
    </div>
</template>

<script setup>
import { ref, nextTick, onMounted, onUnmounted } from 'vue'

// Define props for dynamic images
const props = defineProps({
    productImages: {
        type: Array,
        default: () => [
            '/assets/images/decaf-img-1.webp',
            '/assets/images/decaf-img-2.webp',
            '/assets/images/decaf-img-3.webp',
            '/assets/images/decaf-img-4.webp',
            '/assets/images/decaf-img-5.webp',
            '/assets/images/decaf-img-6.webp',
            '/assets/images/decaf-img-7.webp',
            '/assets/images/decaf-img-8.webp',
            '/assets/images/decaf-img-9.webp',
            '/assets/images/decaf-img-10.webp',
        ]
    },
    productImageAlt: {
        type: String,
        default: 'Product image'
    }
});

// Add Builder.io component definition
defineBuilderComponent({
    friendlyName: 'SwiperProductGallery',
    inputs: [
        {
            name: 'productImages',
            type: 'list',
            friendlyName: 'Product Images',
            defaultValue: [
                '/assets/images/decaf-img-1.webp',
                '/assets/images/decaf-img-2.webp',
                '/assets/images/decaf-img-3.webp',
                '/assets/images/decaf-img-4.webp',
                '/assets/images/decaf-img-5.webp',
                '/assets/images/decaf-img-6.webp',
                '/assets/images/decaf-img-7.webp',
                '/assets/images/decaf-img-8.webp',
                '/assets/images/decaf-img-9.webp',
                '/assets/images/decaf-img-10.webp',
            ],
            subFields: [
                {
                    name: 'value',
                    type: 'file',
                    friendlyName: 'Image',
                    allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp']
                }
            ]
        },
        {
            name: 'productImageAlt',
            type: 'text',
            friendlyName: 'Product Image Alt Text',
            defaultValue: 'Product image'
        }
    ]
});

// Setup state
const swiperIndex1 = ref(0)
const swiperIndex2 = ref(0)
const swiper1 = ref(null)
const swiper2 = ref(null)
const isVisible = ref(false)
const galleryRef = ref(null)
const windowWidth = ref(0)
let observer = null

// Navigation methods
const nextSlide = () => {
    if (swiper2.value && typeof swiper2.value.slideNext === 'function') {
        swiper2.value.slideNext()
    }
}

const prevSlide = () => {
    if (swiper2.value && typeof swiper2.value.slidePrev === 'function') {
        swiper2.value.slidePrev()
    }
}

// Initialize swipers and sync them
const initSwipers = () => {
    if (!swiper1.value || !swiper2.value) return; 

    try {
        // Set up first swiper events (thumbnails)
        if (typeof swiper1.value.on === 'function') {
            swiper1.value.off('slideChange')
            swiper1.value.on('slideChange', () => {
                if (swiper1.value && typeof swiper1.value.activeIndex !== 'undefined') {
                    swiperIndex1.value = swiper1.value.activeIndex
                    
                    if (swiper2.value && typeof swiper2.value.slideTo === 'function' &&
                        swiperIndex2.value !== swiper1.value.activeIndex) {
                        swiperIndex2.value = swiper1.value.activeIndex
                        swiper2.value.slideTo(swiper1.value.activeIndex)
                    }
                }
            })
        }
        
        // Set up second swiper events (main images)
        if (typeof swiper2.value.on === 'function') {
            swiper2.value.off('slideChange')
            swiper2.value.on('slideChange', () => {
                if (swiper2.value && typeof swiper2.value.activeIndex !== 'undefined') {
                    swiperIndex2.value = swiper2.value.activeIndex
                    
                    if (swiper1.value && typeof swiper1.value.slideTo === 'function' &&
                        swiperIndex1.value !== swiper2.value.activeIndex) {
                        swiperIndex1.value = swiper2.value.activeIndex
                        swiper1.value.slideTo(swiper2.value.activeIndex)
                    }
                }
            })
        }
    } catch (error) {
        console.error('Error initializing swiper events:', error)
    }
}

// Handle swiper initialization
const onSwiper1 = (swiperInstance) => {
    if (!swiperInstance) return; 
    swiper1.value = swiperInstance
    
    setTimeout(() => {
        if (swiper1.value && swiper2.value) initSwipers()
    }, 100)
}

const onSwiper2 = (swiperInstance) => {
    if (!swiperInstance) return; 
    swiper2.value = swiperInstance
    
    setTimeout(() => {
        if (swiper1.value && swiper2.value) initSwipers()
    }, 100)
}

// Handle slide changing
const changeSlide = (index) => {
    if (typeof index !== 'number') return; 

    swiperIndex1.value = index
    swiperIndex2.value = index

    if (swiper1.value && typeof swiper1.value.slideTo === 'function') {
        try {
            swiper1.value.slideTo(index)
        } catch (error) {
            console.error('Error changing slide on swiper1:', error)
        }
    }

    if (swiper2.value && typeof swiper2.value.slideTo === 'function') {
        try {
            swiper2.value.slideTo(index)
        } catch (error) {
            console.error('Error changing slide on swiper2:', error)
        }
    }
}

// Update window width
const updateWindowWidth = () => {
    windowWidth.value = window.innerWidth
}

// Setup Intersection Observer to detect when gallery is visible
onMounted(() => {
    // Set initial window width
    updateWindowWidth()
    
    // Add window resize listener
    window.addEventListener('resize', updateWindowWidth)
    
    if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
            const [entry] = entries
            isVisible.value = entry.isIntersecting
            
            // Free up memory if component stays visible as we no longer need to observe
            if (entry.isIntersecting) {
                if (galleryRef.value && observer) {
                    observer.unobserve(galleryRef.value)
                }
            }
        }, {
            root: null, // Use viewport as default
            threshold: 0.1, // Load when at least 10% of element is visible
            rootMargin: '100px' // Small buffer to load slightly before fully visible
        })
        
        if (galleryRef.value) {
            observer.observe(galleryRef.value)
        }
    } else {
        // Fallback for browsers that don't support IntersectionObserver
        isVisible.value = true
    }
})

// Clean up observer on unmount
onUnmounted(() => {
    if (observer && galleryRef.value) {
        observer.unobserve(galleryRef.value)
        observer = null
    }
    
    // Remove window resize listener
    window.removeEventListener('resize', updateWindowWidth)
})
</script>

<style scoped>
@import '@/swiper-bundle.css';

.swiper-wrapper {
    @apply flex items-center;
}

.product-gallery {
    padding: 10px;
}

.main-slider-container {
    position: relative;
}

.main-image {
    display: block;
}

.thumbnail-wrapper {
    @apply transition-all duration-200 ease-in-out;
}

.active-thumbnail {
    @apply border-[#ff6b6b] border-2 !important;
}

.swiper-top .swiper-slide {
    @apply flex md:min-h-[544px] m-auto;
}

.sliper-bottom .swiper-slide {
    @apply flex h-auto md:h-[121px] border border-[#D9D9D9];
}

.thumbnail-image {
    border-radius: 2px;
}

.swiper-button-next-product:after,
.swiper-button-prev-product:after {
    width: 24px;
    height: 30px;
    color: #956449 !important;
    display: inline-block;
}

.swiper-button-next-product:after {
    position: relative;
    bottom: 5px;
}

/* Prevent blue selection */
.swiper, 
.swiper-slide, 
.swiper-wrapper,
.swiper-container,
.main-image,
.thumbnail-image,
.thumbnail-wrapper,
.swiper-button-prev-product,
.swiper-button-next-product {
    -webkit-tap-highlight-color: transparent !important;
    -webkit-touch-callout: none !important;
    @apply select-none outline-none;
}

.thumbnail-slide:active .thumbnail-wrapper {
    @apply scale-[0.98];
}

.swiper-button-prev-product:active svg,
.swiper-button-next-product:active svg {
    @apply scale-95;
}

/* Placeholder styling that can't be done with Tailwind */
.placeholder-thumbs::before,
.placeholder-thumbs::after {
    content: "";
    @apply flex-1 bg-gray-100 border border-[#D9D9D9] rounded;
}
</style>

